/**
 * Sprint 17 B8: DM Broadcast Composer
 * Audience selector, template picker, compliance check, schedule/send.
 */
import { useState, useCallback } from "react";
import { ShieldCheck, Users, MessageSquare, Calendar, Send } from "lucide-react";
import { useApi, useApiMutation } from "@/hooks/use-api";
import { formatDateTime } from "@/lib/i18n";

type BroadcastPlatform = "whatsapp" | "instagram" | "messenger" | "sms";

const PLATFORMS: Array<{ id: BroadcastPlatform; label: string; color: string }> = [
  { id: "whatsapp", label: "WhatsApp", color: "text-[var(--color-success)]" },
  { id: "instagram", label: "Instagram", color: "text-[var(--color-ai)]" },
  { id: "messenger", label: "Messenger", color: "text-[var(--color-primary)]" },
  { id: "sms", label: "SMS", color: "text-[var(--color-info)]" },
];

export default function DmBroadcastPage() {
  const [platform, setPlatform] = useState<BroadcastPlatform>("whatsapp");
  const [message, setMessage] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [complianceVerified, setComplianceVerified] = useState(false);
  const { data: segments } = useApi<Array<{ id: string; name: string; cachedCount?: number | null }>>("/crm/segments");
  const { mutate: sendBroadcast, isLoading, error, data } = useApiMutation<
    { platform: BroadcastPlatform; message: string; audienceTag: string; scheduleAt: string | null },
    { data: { id: string; name: string; status: string; scheduledAt: string | null } }
  >("/messaging/dm/broadcasts");

  const selectedSegment = segments?.find((segment) => segment.id === segmentId) ?? null;

  const handleSend = useCallback(async () => {
    if (!message.trim() || !complianceVerified || !selectedSegment) return;
    await sendBroadcast({
      platform,
      message: message.trim(),
      audienceTag: selectedSegment.name,
      scheduleAt: scheduleAt || null,
    });
  }, [platform, message, scheduleAt, complianceVerified, selectedSegment, sendBroadcast]);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">DM Broadcast</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">Send a message to a group of contacts across any channel.</p>
      </div>

      {/* Platform selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-foreground)]">Channel</label>
        <div className="flex gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlatform(p.id)}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors ${
                platform === p.id
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                  : "border-[var(--color-border)] hover:bg-[var(--color-surface)]"
              }`}
            >
              <MessageSquare className={`h-4 w-4 ${p.color}`} />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Audience */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-foreground)] flex items-center gap-2">
          <Users className="h-4 w-4 text-[var(--color-muted-foreground)]" /> Audience
        </label>
        <select
          value={segmentId}
          onChange={(e) => setSegmentId(e.target.value)}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          <option value="">Select a CRM segment</option>
          {(segments ?? []).map((segment) => (
            <option key={segment.id} value={segment.id}>
              {segment.name}{typeof segment.cachedCount === "number" ? ` (${segment.cachedCount})` : ""}
            </option>
          ))}
        </select>
        {(segments?.length ?? 0) === 0 ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Create a CRM segment first, then return here to bind the broadcast audience.
          </p>
        ) : null}
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-foreground)]">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Type your broadcast message..."
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
        <div className="text-xs text-[var(--color-muted-foreground)]">{message.length} / 1000 characters</div>
      </div>

      {/* Schedule */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-foreground)] flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[var(--color-muted-foreground)]" /> Schedule (optional)
        </label>
        <input
          type="datetime-local"
          value={scheduleAt}
          onChange={(e) => setScheduleAt(e.target.value)}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </div>

      {/* Compliance */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={complianceVerified}
            onChange={(e) => setComplianceVerified(e.target.checked)}
            className="rounded border-[var(--color-border)]"
          />
          <ShieldCheck className={`h-5 w-5 ${complianceVerified ? "text-[var(--color-success)]" : "text-[var(--color-muted-foreground)]"}`} />
          <div>
            <div className="text-sm font-medium text-[var(--color-foreground)]">Compliance verification</div>
            <div className="text-xs text-[var(--color-muted-foreground)]">I confirm all recipients have opted in to receive messages on this channel.</div>
          </div>
        </label>
      </div>

      {(error || data?.data) && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${
          error
            ? "border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 text-[var(--color-warning)]"
            : "border-[var(--color-success)]/20 bg-[var(--color-success)]/5 text-[var(--color-success)]"
        }`}>
          {error
            ? error
            : data?.data.scheduledAt
              ? `Broadcast scheduled for ${formatDateTime(data.data.scheduledAt)}.`
              : "Broadcast draft created. Review or schedule it from the campaign workspace."}
        </div>
      )}

      {/* Send */}
      <button
        type="button"
        onClick={handleSend}
        disabled={isLoading || !message.trim() || !complianceVerified || !selectedSegment}
        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-6 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
      >
        <Send className="h-4 w-4" />
        {scheduleAt ? "Schedule Broadcast" : "Create Draft"}
      </button>
    </div>
  );
}
